import { useQuery } from '@apollo/client';
import {
  getAnchorHrefFromText,
  splitTextByElementNodes,
} from '@loomhq/createhq/dist/video-player/utils';
import React, { useMemo } from 'react';

import { ID_REGEX } from 'constants/regex';
import { sanitizeContent } from 'utilities/comments';
import {
  Mention as MentionRecord,
  getSemanticCommentParts,
} from 'utilities/mention';
import { unicodeSlice } from 'utilities/unicode/unicodeSlice';

import MentionPill from '../mention-pill';
import GetUserWithProfileInfo from '../../user-profile/profile-card/GetUserWithProfileInfo.graphql';
import { getVideoIdFromSharePageUrl } from '../../../../../shared/utilities/url';

import { EmbedVideo } from 'components/video-player-fresh';
import { useFetchVideoData } from 'components/video-player-fresh/hooks';
import { AspectRatio } from 'components/video-player-fresh/layers';

/**
 * @param {Object} props
 * @param {MentionRecord} props.mention
 * @param {Boolean} props.withPills
 * @returns {React.ReactElement}
 *  the mention instance from shared/utilities/mention.js dedicated to Workspace Contacts
 */
const WorkspaceContactMention = ({ mention: { displayText }, withPills }) =>
  !withPills ? (
    <>{`@${displayText}`}</>
  ) : (
    <MentionPill key={displayText} teamMember={undefined} name={displayText} />
  );

/**
 * @param {Object} props
 * @param {MentionRecord} props.mention
 * @param {Boolean} props.withPills
 * @returns {React.ReactElement}
 *  the mention instance from shared/utilities/mention.js
 */
const Mention = props => {
  const { mention } = props;
  const { displayText } = mention;

  const MentionComponent = MENTIONS[mention.modelType];

  if (MentionComponent) {
    return <MentionComponent {...props} />;
  }

  return <>{`@${displayText}`}</>;
};

/**
 * @param {Object} props
 * @param {MentionRecord} props.mention
 * @param {Boolean} props.withPills
 * @returns {React.ReactElement}
 *  the mention instance from shared/utilities/mention.js dedicated to Users
 */
const UserMention = ({ mention, withPills }) => {
  const { modelId: userId, displayText } = mention;
  const { loading, error, userProfileData } = UserMention.useComponent({
    userId,
  });

  const shouldRenderPlainText = loading || error || !withPills;
  const finalDisplayName = userProfileData
    ? `${userProfileData.first_name} ${userProfileData.last_name}`
    : displayText;

  return shouldRenderPlainText ? (
    <>{`@${finalDisplayName}`}</>
  ) : (
    <MentionPill
      key={finalDisplayName}
      teamMember={userProfileData}
      name={finalDisplayName}
    />
  );
};

UserMention.useComponent = ({ userId }) => {
  const {
    data: userProfileData,
    loading,
    error,
  } = useQuery(GetUserWithProfileInfo, {
    variables: {
      userId,
    },
  });

  return {
    userProfileData: userProfileData?.user?.user,
    loading,
    error,
  };
};

/**
 * Wrapper that takes videoId prop to embed a loom in a comment
 * @param {Object} props
 * @param {string} props.videoId
 * @returns {React.ReactElement | null}
 */
const CommentEmbedVideoWrapper = ({ videoId }) => {
  const { loading, error, data } = useFetchVideoData(videoId);

  if (loading || error || !data?.video) {
    return null;
  }

  return (
    <AspectRatio>
      <EmbedVideo videoModel={data.video} />
    </AspectRatio>
  );
};

/**
 * @param {Object} props
 * @param {String} props.comment
 * @param {JSX.Element} props.beforeBlock
 * @param {JSX.Element} props.afterBlock
 * @param {Boolean} props.withPills
 * @param {Number} props.trimLength
 * @param {Boolean} props.shouldTrim
 * @param {Boolean} props.useNewEmbedPlayer
 * @returns {React.ReactElement}
 */
const SanitizedAndMentionedContent = ({
  comment,
  beforeBlock,
  afterBlock,
  withPills,
  trimLength,
  shouldTrim,
  useNewEmbedPlayer,
}) => {
  const sContent = sanitizeContent(comment);
  const semanticParts = getSemanticCommentParts(sContent);
  const maxAllowedLength = shouldTrim ? Number.POSITIVE_INFINITY : trimLength;

  const trimmedContent = useMemo(() => {
    const results = [];
    let simpleContentLength = 0;

    for (let idx = 0; idx < semanticParts.length; idx++) {
      const commentPart = semanticParts[idx];
      const isMention = commentPart instanceof MentionRecord;
      const commentPartLength = isMention
        ? commentPart.markup.length
        : commentPart.length;

      if (simpleContentLength + commentPartLength > maxAllowedLength) {
        if (!isMention) {
          const unicodeSafeRemainder = unicodeSlice(
            commentPart,
            0,
            maxAllowedLength - simpleContentLength
          );

          results.push(
            <span dangerouslySetInnerHTML={{ __html: unicodeSafeRemainder }} />
          );
        }

        results.push(<span key="triple-dots">...&nbsp;&nbsp;</span>);
        break;
      }

      simpleContentLength += commentPartLength;

      if (isMention) {
        results.push(
          <Mention mention={commentPart} withPills={withPills} key={idx} />
        );

        continue;
      }

      // Check for loom share URLs to embed
      // For multiple anchor tags, need to split to embed each share URL's loom individually
      const splitCommentParts = splitTextByElementNodes(commentPart);

      splitCommentParts.forEach((part, splitIndex) => {
        if (useNewEmbedPlayer) {
          const anchorHrefString = getAnchorHrefFromText(part);
          const videoId = getVideoIdFromSharePageUrl(anchorHrefString);

          if (videoId && ID_REGEX.test(videoId)) {
            results.push(
              <CommentEmbedVideoWrapper
                videoId={videoId}
                key={`wrapper-${part}-${idx}-${splitIndex}`}
              />
            );
          }
        }

        results.push(
          <span key={`${part}-${idx}-${splitIndex}`}>
            <span dangerouslySetInnerHTML={{ __html: part }} />
          </span>
        );
      });
    }

    return results;
  }, [maxAllowedLength, semanticParts, withPills, useNewEmbedPlayer]);

  return (
    <>
      {beforeBlock}
      {trimmedContent}
      {afterBlock}
    </>
  );
};

const FILTERED_OUT_PROPS = [
  'workspaceTeamMembers',
  'withPills',
  'content',
  'forwardRef',
  'trimLength',
  'beforeBlock',
  'afterBlock',
  'shouldTrim',
  'useNewEmbedPlayer',
];
const MentionParser = props => {
  const {
    withPills,
    comment,
    beforeBlock,
    afterBlock,
    trimLength,
    shouldTrim,
    forwardRef: embedRef,
    useNewEmbedPlayer,
  } = props;

  const filteredProps = Object.keys(props)
    .filter(k => !FILTERED_OUT_PROPS.includes(k))
    .reduce((newProps, key) => ({ ...newProps, [key]: props[key] }), {});

  return (
    <div {...filteredProps} ref={embedRef}>
      <SanitizedAndMentionedContent
        comment={comment}
        beforeBlock={beforeBlock}
        afterBlock={afterBlock}
        withPills={withPills}
        trimLength={trimLength}
        shouldTrim={shouldTrim}
        useNewEmbedPlayer={useNewEmbedPlayer}
      />
    </div>
  );
};

const MENTIONS = {
  user: UserMention,
  workspace_contacts: WorkspaceContactMention,
};

export default MentionParser;

export const exportedForTesting = {
  UserMention,
  WorkspaceContactMention,
};
